import { format } from 'app/shared/StatFormatting';
import { Cesium } from 'app/cesium';
import { IpDeviceStatsResource } from 'app/shared/resources';
import { ImsObject } from 'app/shared/collections/ims_object';
import { RadioStat } from './radio_stat';
import { toUnix } from 'app/shared/utilities/time';
import { VisibleRadio } from 'app/views/view_types/network/collections/visible_radio';
import { Radio } from 'app/shared/collections/radio';

export const IMS_TYPE__IP_DEVICE_STAT = 'ip_device_stat';

// The stat definition here tells IMS how to display the stat

export class IpDeviceStat extends ImsObject {
    private readonly _radioStats: RadioStat[];
    private readonly _nextHop: VisibleRadio;
    private readonly _activeRadioStat: RadioStat;
    private readonly _toRadio: Radio;
    private _timestampAsJulian: Cesium.JulianDate;
    private readonly _timestampDate: Date;
    private readonly _timestampUnix: number;

    constructor(radioCollection, resource = new IpDeviceStatsResource()) {
        super(IMS_TYPE__IP_DEVICE_STAT, resource);

        this._radioStats =
            (this.resource.radio_stats &&
                this.resource.radio_stats.map((radioStat) => new RadioStat(radioStat))) ||
            [];

        const { next, active, toRadio } = this._getNextActiveHop(radioCollection);
        this._nextHop = next;
        this._activeRadioStat = active;
        this._toRadio = toRadio;

        // Lazy evaluated in the public getter
        this._timestampAsJulian = undefined;
        this._timestampDate = new Date(this.resource.timestamp);
        this._timestampUnix = toUnix(this._timestampDate);
    }

    _getNextActiveHop(radioCollection) {
        const activeRadio = this.radioStats.find((radioStat) => radioStat.is_active) || null;
        const nextHop =
            (activeRadio && activeRadio.visibleRadios.find((visible) => visible.isNextHop)) || null;

        return {
            active: activeRadio,
            next: nextHop,
            toRadio: nextHop ? radioCollection.getRadioByMAC(nextHop.mac) : null,
        };
    }

    get id() {
        return this.resource.id;
    }

    formatted(
        timeZone: string,
        throughputAsBits: boolean,
    ): {
        ipDeviceId: number;
        ip_device_id: number;
        timestamp: string;
        connectivity: string;
        latency: string;
        packets1: string;
        packets2: string;
        packets3: string;
        packets4: string;
        packets5: string;
        packets6: string;
        packets7: string;
        packets8: string;
        radioStats: Array<any>;
    } {
        return {
            ipDeviceId: this.ipDeviceId,
            ip_device_id: this.ipDeviceId, // Used during info box rendering to make this look like a dynamic stat
            timestamp: format.timestamp(this.timestamp, timeZone),
            connectivity: format.connectivity(this.connectivity),
            latency: format.latency(this.latency),
            packets1: format.packets(this.packets1),
            packets2: format.packets(this.packets2),
            packets3: format.packets(this.packets3),
            packets4: format.packets(this.packets4),
            packets5: format.packets(this.packets5),
            packets6: format.packets(this.packets6),
            packets7: format.packets(this.packets7),
            packets8: format.packets(this.packets8),
            radioStats: this.radioStats.map((radioStat) => {
                return radioStat.formatted(throughputAsBits);
            }),
        };
    }

    get nextHop() {
        return this._nextHop;
    }

    get activeRadioStat() {
        return this._activeRadioStat;
    }

    get toRadio() {
        return this._toRadio;
    }

    get radioStats(): RadioStat[] {
        return this._radioStats;
    }

    get ipDeviceId() {
        return this.resource.ip_device_id;
    }

    get timestampDate(): Date {
        return this._timestampDate;
    }

    get timestampUnix(): number {
        return this._timestampUnix;
    }

    get timestamp(): string {
        return this.resource.timestamp;
    }

    get timestampAsJulian() {
        if (this._timestampAsJulian) {
            return this._timestampAsJulian;
        }
        this._timestampAsJulian = Cesium.JulianDate.fromDate(this.timestampDate);

        return this._timestampAsJulian;
    }

    get latency() {
        return this.resource.latency;
    }

    get connectivity() {
        return this.resource.connectivity;
    }

    get packets1() {
        return this.resource.packets1;
    }

    get packets2() {
        return this.resource.packets2;
    }

    get packets3() {
        return this.resource.packets3;
    }

    get packets4() {
        return this.resource.packets4;
    }

    get packets5() {
        return this.resource.packets5;
    }

    get packets6() {
        return this.resource.packets6;
    }

    get packets7() {
        return this.resource.packets7;
    }

    get packets8() {
        return this.resource.packets8;
    }

    get activeRadioNoise() {
        return this.activeRadioStat ? this.activeRadioStat.noise : undefined;
    }

    get activeRadioRSSI() {
        return this.activeRadioStat ? this.activeRadioStat.avg_rssi : undefined;
    }

    get activeRadioSNR() {
        return this.activeRadioStat ? this.activeRadioStat.snr : undefined;
    }

    get activeRadioTxRate() {
        return this.activeRadioStat ? this.activeRadioStat.avg_tx_rate : undefined;
    }

    get activeRadioRxRate() {
        return this.activeRadioStat ? this.activeRadioStat.avg_rx_rate : undefined;
    }

    get activeRadioChannel() {
        return this.activeRadioStat ? this.activeRadioStat.channel : undefined;
    }

    get nextHopMac() {
        return this._nextHop ? this._nextHop.mac : undefined;
    }

    get isIgnored() {
        return this.resource.ignore_stat;
    }
}
